import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { GenericCardsGrid } from '../GenericDeviceCard';
import { StyledTablePagination } from '../TableComponent/styles';

export interface CardPaginationProps<T> {
  /** Full (unpaginated) set of items to render as cards */
  data: T[];
  /** Renders a single item; the returned element must set its own `key` */
  renderItem: (item: T) => ReactNode;
  /** Shown instead of the grid while data is loading */
  loading?: boolean;
  /** Renders one skeleton placeholder; called once per estimated card slot */
  renderSkeleton?: (index: number) => ReactNode;
  /** Gap forwarded to the underlying GenericCardsGrid */
  gap?: string;
  /** Rows of cards to fit per page (columns are computed from container width) */
  targetRows?: number;
  /** Approximate card width (px) used together with cardGap to estimate columns */
  cardWidth?: number;
  /** Approximate gap (px) between cards, used for the column estimate */
  cardGap?: number;
  /** Controlled page index. Omit to let the component manage its own state */
  page?: number;
  /** Required when `page` is controlled */
  onPageChange?: (page: number) => void;
  /** Server-side pagination: total items, with `data` already the current page */
  totalCount?: number;
  /** Notified whenever the cards per page change, to request that page size */
  onPageSizeChange?: (size: number) => void;
  sx?: React.CSSProperties;
}

function CardPagination<T>({
  data,
  renderItem,
  loading = false,
  renderSkeleton,
  gap = '18px',
  targetRows = 2,
  cardWidth = 340,
  cardGap = 20,
  page: controlledPage,
  onPageChange,
  totalCount,
  onPageSizeChange,
  sx,
}: CardPaginationProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [internalPage, setInternalPage] = useState(0);
  const [columns, setColumns] = useState(1);

  const page = controlledPage ?? internalPage;
  const setPage = onPageChange ?? setInternalPage;

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const width = entry.contentRect.width;
        const calculatedCols = Math.floor((width + cardGap) / cardWidth) || 1;
        setColumns(calculatedCols);
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [cardWidth, cardGap]);

  const dynamicRowsPerPage = columns * targetRows;
  const count = totalCount ?? data.length;

  useEffect(() => {
    onPageSizeChange?.(dynamicRowsPerPage);
  }, [dynamicRowsPerPage, onPageSizeChange]);

  useEffect(() => {
    // While loading the count is stale, so don't clamp the page against it
    if (loading) return;
    const maxPage = Math.max(0, Math.ceil(count / dynamicRowsPerPage) - 1);
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [loading, count, dynamicRowsPerPage, page, setPage]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const paginatedData =
    totalCount !== undefined
      ? data
      : data.slice(
          page * dynamicRowsPerPage,
          page * dynamicRowsPerPage + dynamicRowsPerPage
        );

  // A single container in both states keeps the ResizeObserver attached
  return (
    <div
      ref={containerRef}
      style={
        loading
          ? { width: '100%' }
          : { width: '100%', marginTop: '16px', ...sx }
      }
    >
      {loading ? (
        <GenericCardsGrid gap={gap} columns={columns}>
          {renderSkeleton &&
            [...Array(dynamicRowsPerPage || targetRows)].map((_, index) => (
              <div key={index}>{renderSkeleton(index)}</div>
            ))}
        </GenericCardsGrid>
      ) : (
        <>
          <GenericCardsGrid gap={gap} columns={columns}>
            {paginatedData.map((item) => renderItem(item))}
          </GenericCardsGrid>
          <StyledTablePagination
            component="div"
            count={count}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={dynamicRowsPerPage}
            rowsPerPageOptions={[dynamicRowsPerPage]}
          />
        </>
      )}
    </div>
  );
}

export default CardPagination;
